'use client';

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useState,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { getSession, signIn } from 'next-auth/react';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
  Button,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from '@mui/material';

import { addToQueryString, isStaging } from '~/shared';

import { StyledLoginForm } from './elements';

// TODO: find a way to remove dependency cycle on MartinsTokenRefreshSession because of AuthProviders export from campaign entity
enum AuthProviders {
  // AZURE_AD = 'azure-ad',
  MARTINS_CRED = 'martins-credentials',
  REFRESH_TOKEN = 'refresh-token',
  EXPIRED_MARTINS_CRED = 'expired-martins-credentials',
  FABRICA_CRED = 'fabrica-credentials',
}

export function LoginForm({
  callbackUrl,
  errorParam,
  isSubmitting,
  setIsSubmitting,
  onLoginSuccess,
  headerTitle,
  headerDescription,
  mfaHeaderTitle,
  mfaHeaderDescription,
  onStepChange,
  onRecoveryRequest,
}: {
  callbackUrl?: string;
  errorParam?: string;
  isSubmitting: `${AuthProviders}` | null;
  setIsSubmitting: Dispatch<SetStateAction<`${AuthProviders}` | null>>;
  onLoginSuccess?: () => void;
  headerTitle?: string;
  headerDescription?: string;
  mfaHeaderTitle?: string;
  mfaHeaderDescription?: string;
  onStepChange?: (step: 'credentials' | 'mfa') => void;
  onRecoveryRequest?: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const addSearchParam = useCallback(addToQueryString, [searchParams]);

  const [step, setStep] = useState<'credentials' | 'mfa'>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  const isEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  function handleValidation() {
    setUsernameError(username === '' ? 'Digite um usuário' : '');
    setPasswordError(password === '' ? 'Digite uma senha' : '');
    return username !== '' && password !== '';
  }

  function handleMfaValidation() {
    setMfaError(mfaCode === '' ? 'Digite o código MFA' : '');
    return mfaCode !== '';
  }

  async function handleSignIn(skipMfa = false) {
    if (!skipMfa && !handleMfaValidation()) {
      return;
    }
    const provider = isEmail(username)
      ? AuthProviders.FABRICA_CRED
      : AuthProviders.MARTINS_CRED;

    setIsSubmitting(provider);
    if (!handleValidation()) {
      setIsSubmitting(null);
      return;
    }
    try {
      if (provider === AuthProviders.FABRICA_CRED) {
        const result = await signIn(provider, {
          username,
          password,
          redirect: false,
        });

        if (result?.error) {
          const errorParams = addSearchParam(
            'error',
            result.error,
            searchParams.toString()
          );
          router.push(`${pathname}?${errorParams}`);
          setIsSubmitting(null);
          return;
        }

        const session = await getSession();
        if (session?.user?.firstAccess) {
          window.location.href = '/primeiro-acesso';
        } else {
          onLoginSuccess?.();
          window.location.href = callbackUrl || '/';
        }
        return;
      }

      await signIn(provider, {
        username,
        password,
        mfaCode,
        callbackUrl,
        redirect: !!callbackUrl,
      });

      onLoginSuccess?.();
      setIsSubmitting(null);
    } catch (error) {
      const urlParams = addSearchParam(
        'error',
        'SERVICE_UNAVAILABLE',
        searchParams.toString()
      );
      router.push(`${pathname}?${urlParams}`);
      setIsSubmitting(null);
    }
  }

  async function handleNextStep() {
    if (!handleValidation()) {
      return;
    }
    if (isEmail(username) || isStaging()) {
      await handleSignIn(true);
    } else {
      setStep('mfa');
      onStepChange?.('mfa');
    }
  }

  if (step === 'credentials') {
    return (
      <StyledLoginForm>
        {headerTitle && (
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', mb: 1, fontWeight: 600 }}
          >
            {headerTitle}
          </Typography>
        )}
        {headerDescription && (
          <Typography
            variant="body2"
            sx={{
              textAlign: 'center',
              color: theme => theme.palette.text.secondary,
              mb: 2,
            }}
          >
            {headerDescription}
          </Typography>
        )}
        {errorParam && !isSubmitting && (
          <Typography sx={{ color: theme => theme.palette.error.light }}>
            {errorParam}
          </Typography>
        )}

        <TextField
          disabled={!!isSubmitting}
          value={username}
          onChange={e => {
            const value = e.target.value.trim();
            setUsername(value);
            setUsernameError(value === '' ? 'Digite um usuário' : '');
          }}
          error={usernameError !== ''}
          helperText={usernameError}
          id="username"
          type="text"
          label="Usuário"
          variant="filled"
        />
        <TextField
          disabled={!!isSubmitting}
          value={password}
          onChange={e => {
            const value = e.target.value.trim();
            setPassword(value);
            setPasswordError(value === '' ? 'Digite uma senha' : '');
          }}
          error={passwordError !== ''}
          helperText={passwordError}
          id="password"
          type={showPassword ? 'text' : 'password'}
          label="Senha"
          variant="filled"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Toggle password visibility"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? (
                    <VisibilityOutlined />
                  ) : (
                    <VisibilityOffOutlined />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          disabled={!!isSubmitting || username === '' || password === ''}
          type="submit"
          color="primary"
          variant="contained"
          sx={{ textTransform: 'none', mt: 2 }}
          onClick={e => {
            e.preventDefault();
            handleNextStep();
          }}
          size="large"
        >
          Acessar Conta
        </Button>

        {onRecoveryRequest ? (
          <Button
            type="button"
            variant="text"
            onClick={onRecoveryRequest}
            sx={{
              alignSelf: 'center',
              fontFamily: '"Rubik", sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              width: '100%',
            }}
            size="large"
          >
            Esqueci minha senha
          </Button>
        ) : (
          <Button>
            <Link
              href="/recuperacao-senha"
              sx={{
                alignSelf: 'center',
                fontFamily: '"Rubik", sans-serif',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              Esqueci minha senha
            </Link>
          </Button>
        )}
      </StyledLoginForm>
    );
  }

  return (
    <StyledLoginForm>
      {mfaHeaderTitle && (
        <Typography
          variant="h6"
          sx={{ textAlign: 'center', mb: 1, fontWeight: 600 }}
        >
          {mfaHeaderTitle}
        </Typography>
      )}
      {mfaHeaderDescription && (
        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            color: theme => theme.palette.text.secondary,
            mb: 2,
          }}
        >
          {mfaHeaderDescription}
        </Typography>
      )}
      {errorParam && !isSubmitting && (
        <Typography sx={{ color: theme => theme.palette.error.light }}>
          {errorParam}
        </Typography>
      )}

      <TextField
        disabled={!!isSubmitting}
        value={mfaCode}
        onChange={e => {
          const value = e.target.value.trim();
          setMfaCode(value);
          setMfaError(value === '' ? 'Digite o código MFA' : '');
        }}
        error={mfaError !== ''}
        helperText={mfaError}
        id="mfaCode"
        type="text"
        label="Codigo MFA"
        variant="filled"
        sx={{ marginBottom: theme => theme.spacing(4) }}
      />

      <Button
        disabled={!!isSubmitting || mfaCode === ''}
        type="submit"
        color="primary"
        variant="contained"
        sx={{ textTransform: 'none' }}
        onClick={e => {
          e.preventDefault();
          handleSignIn();
        }}
        size="large"
      >
        Acessar Conta
      </Button>

      {/* <Link
        href="/recuperacao-senha"

        sx={{ alignSelf: 'center', fontFamily: '"Rubik", sans-serif' }}
      >
        Esqueci minha senha
      </Link> */}
    </StyledLoginForm>
  );
}
